import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { clientActions } from '@core/client/client.action';
import { useClientStore } from '@core/client/useClientStore';
import { useTenantStore } from '@core/tenant/useTenantStore';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import { missingTimesheetActions } from '@core/timesheetApproval/missingTimesheet.action';
import type {
  BulkSubmitMissingTimesheetsInput,
  BulkSubmitMissingTimesheetsResult,
} from '@core/timesheetApproval/missingTimesheet.types';
import { TimesheetApprovalView } from '@core/timesheetApproval/timesheetApproval.types';
import { useTenantId } from '@/contexts/TenantContext';
import Button from '@/components/Button';
import { formatAccountManagerDisplay } from '@/utils/client.constants';
import { getCustomAttribute } from '@/utils/utils';
import Tabs from '@/components/Tabs';
import useFeatureView from '@/hooks/useFeatureView';
import MissingTimesheetDrawer from '@/modules/timesheets/components/MissingTimesheetDrawer';
import MissingTimesheetFilters from '@/modules/timesheets/components/MissingTimesheetFilters';
import PayPeriodSwitcher from '@/modules/timesheets/components/PayPeriodSwitcher';
import TimesheetFilters from '@/modules/timesheets/components/TimesheetFilters';
import TimesheetGroupDrawer from '@/modules/timesheets/components/TimesheetGroupDrawer';
import TimesheetGroupsTable from '@/modules/timesheets/components/TimesheetGroupsTable';
import { useMissingTimesheets } from '@/modules/timesheets/hooks/useMissingTimesheets';
import { useTimesheetApprovals } from '@/modules/timesheets/hooks/useTimesheetApprovals';
import {
  EMPTY_MISSING_TIMESHEET_FILTERS,
  filterMissingTimesheets,
  groupMissingTimesheetsByClient,
  type MissingTimesheetFilters as MissingTimesheetFiltersState,
} from '@/modules/timesheets/utils/missingTimesheets.utils';
import {
  EMPTY_TIMESHEET_FILTERS,
  filterTimesheetItems,
  filterTimesheetsToPayPeriod,
  groupTimesheetsByClient,
  groupTimesheetsByPayCycle,
  isInPayPeriod,
  type PayPeriodSelection,
  selectablePayPeriods,
  type TimesheetFilters as TimesheetFiltersState,
} from '@/modules/timesheets/utils/timesheetGroups.utils';
import {
  getTimesheetsView,
  getTimesheetTabs,
  type TimesheetTabKey,
} from '@/modules/timesheets/views/timesheets.view';

const PAGE_SIZE = 10;

const APPROVAL_EMPTY_MESSAGE: Record<TimesheetApprovalView, string> = {
  [TimesheetApprovalView.Pending]: 'No timesheets to approve',
  [TimesheetApprovalView.Approved]: 'No approved timesheets',
};

const approvalViewForTab = (
  tab: TimesheetTabKey,
): TimesheetApprovalView | null => {
  if (tab === 'requiresApproval') return TimesheetApprovalView.Pending;
  if (tab === 'approved') return TimesheetApprovalView.Approved;
  return null;
};

const TimesheetsPage = () => {
  const view = useFeatureView(getTimesheetsView);
  const tabs = getTimesheetTabs(view.tabs);
  const [tab, setTab] = useState<TimesheetTabKey>(view.defaultTab);
  const [filters, setFilters] = useState<TimesheetFiltersState>(
    EMPTY_TIMESHEET_FILTERS,
  );
  const [missingFilters, setMissingFilters] =
    useState<MissingTimesheetFiltersState>(EMPTY_MISSING_TIMESHEET_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(
    new Set(),
  );
  const [drawerRowKey, setDrawerRowKey] = useState<string | null>(null);
  const tenantId = useTenantId();
  const clients = useClientStore((state) => state.clients);
  const fetchClientById = useClientStore((state) => state.fetchClientById);
  const tenantUsers = useTenantStore((state) => state.tenantUsers);
  const fetchTenantUsersForTenant = useTenantStore(
    (state) => state.fetchTenantUsersForTenant,
  );
  const requestedClientIds = useRef(new Set<string>());
  const [approvingCycleKey, setApprovingCycleKey] = useState<string | null>(
    null,
  );
  const payCycleFrequency = useCoreLoopConfigurationStore(
    (state) => state.coreLoopConfigurationItems?.timesheetSubmissionFrequency,
  );
  const payRunCycleAnchorDate = useCoreLoopConfigurationStore(
    (state) => state.coreLoopConfigurationItems?.payRunCycleAnchorDate,
  );
  const payRunSettings = useMemo(
    () => ({ frequency: payCycleFrequency, anchorDate: payRunCycleAnchorDate }),
    [payCycleFrequency, payRunCycleAnchorDate],
  );
  // Every tab shows one pay period. The previous one is the default, since
  // that is the period being submitted, approved and paid.
  const [payPeriodSelection, setPayPeriodSelection] =
    useState<PayPeriodSelection>('previous');
  const payPeriods = useMemo(
    () => selectablePayPeriods(payRunSettings),
    [payRunSettings],
  );
  const selectedPayPeriod = payPeriods[payPeriodSelection];

  // TEMP: check the tenant's pay-run config while testing pay periods.
  useEffect(() => {
    console.log('[Timesheets] core loop pay-run config', {
      timesheetSubmissionFrequency: payCycleFrequency,
      payRunCycleAnchorDate,
      payPeriods,
    });
  }, [payCycleFrequency, payRunCycleAnchorDate, payPeriods]);
  const isPendingTab = tab === 'pending';
  const approvalView = approvalViewForTab(tab);
  const {
    items,
    isLoading,
    isRefreshing,
    isFetchingMore,
    error,
    refetch,
    approve,
  } = useTimesheetApprovals(approvalView);
  const {
    items: missingItems,
    isLoading: isMissingLoading,
    isRefreshing: isMissingRefreshing,
    error: missingError,
    refetch: refetchMissing,
  } = useMissingTimesheets(
    view.query.missingTimesheets.fetch && isPendingTab,
    // Both periods load together, so switching between them is instant.
    payPeriods.previous.startDateISO,
    payPeriods.current.endDateISO,
  );
  const canApprove =
    tab === 'requiresApproval' && view.actions.approveTimesheets;

  const accountManagerByClientId = useMemo(() => {
    const names = new Map<string, string>();
    clients.forEach((client) => {
      const accountManager = getCustomAttribute(client, 'accountManager');
      if (!accountManager) {
        return;
      }

      const display = formatAccountManagerDisplay(accountManager, tenantUsers);

      if (display !== '-') {
        names.set(client.id, display);
      }
    });
    return names;
  }, [clients, tenantUsers]);

  const periodItems = useMemo(
    () => filterTimesheetsToPayPeriod(items, selectedPayPeriod),
    [items, selectedPayPeriod],
  );
  const periodMissingItems = useMemo(
    () =>
      missingItems.filter((item) =>
        isInPayPeriod(item.shiftDate, selectedPayPeriod),
      ),
    [missingItems, selectedPayPeriod],
  );
  const approvalGroups = useMemo(
    () =>
      groupTimesheetsByClient(
        filterTimesheetItems(periodItems, filters, accountManagerByClientId),
      ),
    [periodItems, filters, accountManagerByClientId],
  );
  const missingGroups = useMemo(
    () =>
      groupMissingTimesheetsByClient(
        filterMissingTimesheets(
          periodMissingItems,
          missingFilters,
          accountManagerByClientId,
        ),
      ),
    [periodMissingItems, missingFilters, accountManagerByClientId],
  );
  const groups = isPendingTab ? missingGroups : approvalGroups;
  const totalPages = Math.ceil(groups.length / PAGE_SIZE);
  const pageGroups = groups.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const startIndex =
    groups.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, groups.length);
  const drawerGroup =
    approvalGroups.find((group) => group.rowKey === drawerRowKey) ?? null;
  const missingDrawerGroup =
    missingGroups.find((group) => group.rowKey === drawerRowKey) ?? null;
  const clientIds = useMemo(() => {
    const ids = new Set<string>();
    items.forEach((item) => {
      if (item.clientId) {
        ids.add(item.clientId);
      }
    });
    missingItems.forEach((item) => {
      if (item.clientId) {
        ids.add(item.clientId);
      }
    });
    return [...ids];
  }, [items, missingItems]);
  const activeError = isPendingTab ? missingError : error;
  const activeLoading = isPendingTab ? isMissingLoading : isLoading;

  useEffect(() => {
    if (!view.tabs.includes(tab)) {
      setTab(view.defaultTab);
    }
  }, [view.tabs, view.defaultTab, tab]);

  // Reset paging and selection when the visible rows change underneath them.
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRowKeys(new Set());
  }, [tab, filters, missingFilters, payPeriodSelection]);

  useEffect(() => {
    setDrawerRowKey(null);
  }, [tab]);

  useEffect(() => {
    if (!view.actions.showClientName || !tenantId) return;
    fetchTenantUsersForTenant(tenantId);
  }, [tenantId, view.actions.showClientName, fetchTenantUsersForTenant]);

  useEffect(() => {
    if (!view.actions.showClientName) return;
    clientIds.forEach((clientId) => {
      if (clients.some((client) => client.id === clientId)) return;
      if (requestedClientIds.current.has(clientId)) return;
      requestedClientIds.current.add(clientId);
      void fetchClientById(clientId);
    });
  }, [clientIds, clients, fetchClientById, view.actions.showClientName]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (activeError) {
      toast.error(activeError);
    }
  }, [activeError]);

  const approveDaySequenceIds = async (cycleKey: string, ids: string[]) => {
    if (ids.length === 0) return;

    setApprovingCycleKey(cycleKey);
    try {
      const approvedCount = await approve(ids);
      if (approvedCount === ids.length) {
        toast.success(
          `${approvedCount} ${approvedCount === 1 ? 'timesheet' : 'timesheets'} approved`,
        );
      } else if (approvedCount > 0) {
        toast.warning(
          `${approvedCount} of ${ids.length} timesheets approved. Please review the rest.`,
        );
      } else {
        toast.error('Failed to approve timesheets');
      }
      // A failed batch can still have approved some timesheets, so reload.
      if (approvedCount !== ids.length) {
        await refetch();
      }
      setSelectedRowKeys(new Set());
    } finally {
      setApprovingCycleKey(null);
    }
  };

  const toggleGroup = (rowKey: string) =>
    setSelectedRowKeys((current) => {
      const next = new Set(current);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });

  const toggleAll = (selected: boolean) =>
    setSelectedRowKeys(
      selected ? new Set(pageGroups.map((group) => group.rowKey)) : new Set(),
    );

  const selectedGroups = approvalGroups.filter((group) =>
    selectedRowKeys.has(group.rowKey),
  );

  const approveSelectedGroups = () => {
    const selectedItems = selectedGroups.flatMap((group) => group.items);
    const payCycles = groupTimesheetsByPayCycle(
      selectedItems,
      payRunSettings,
      'asc',
    );
    if (payCycles.length !== 1) {
      toast.error(
        'Approve one pay cycle at a time. Open a candidate to approve each cycle.',
      );
      return;
    }
    void approveDaySequenceIds(
      payCycles[0].key,
      selectedItems.map((item) => item.daySequence.id),
    );
  };

  const submitMissingTimesheets = async (
    submission: BulkSubmitMissingTimesheetsInput,
  ): Promise<BulkSubmitMissingTimesheetsResult> => {
    const result =
      await missingTimesheetActions.bulkSubmitMissingTimesheets(submission);
    await refetchMissing();
    return result;
  };

  const payPeriodSwitcher = (
    <PayPeriodSwitcher
      periods={payPeriods}
      selected={payPeriodSelection}
      onChange={setPayPeriodSelection}
    />
  );

  return (
    <div className="flex h-full w-full flex-col p-6">
      <h1 className="mb-4 text-xl font-semibold text-gray-900">Timesheets</h1>
      <Tabs
        tabs={tabs}
        selectedTabKey={tab}
        onTabChange={(key) => setTab(key as TimesheetTabKey)}
      >
        <div className="space-y-4">
          {isPendingTab ? (
            <MissingTimesheetFilters
              items={periodMissingItems}
              leading={payPeriodSwitcher}
              filters={missingFilters}
              showClientFilter={view.actions.showClientFilter}
              showAccountManagerFilter={view.actions.showClientName}
              accountManagerByClientId={accountManagerByClientId}
              onChange={setMissingFilters}
            />
          ) : (
            <TimesheetFilters
              items={periodItems}
              leading={payPeriodSwitcher}
              filters={filters}
              showClientFilter={view.actions.showClientFilter}
              showAccountManagerFilter={view.actions.showClientName}
              accountManagerByClientId={accountManagerByClientId}
              onChange={setFilters}
            />
          )}
          <TimesheetGroupsTable
            groups={pageGroups}
            isLoading={activeLoading}
            emptyMessage={
              approvalView
                ? APPROVAL_EMPTY_MESSAGE[approvalView]
                : 'No timesheets awaiting submission'
            }
            showClientColumns={view.actions.showClientName}
            accountManagerByClientId={accountManagerByClientId}
            selectable={canApprove}
            selectedRowKeys={selectedRowKeys}
            onToggleGroup={toggleGroup}
            onToggleAll={toggleAll}
            onRowClick={(group) => setDrawerRowKey(group.rowKey)}
          />
          {canApprove && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="primary"
                disabled={
                  selectedGroups.length === 0 || approvingCycleKey !== null
                }
                isLoading={approvingCycleKey !== null}
                onClick={approveSelectedGroups}
                className="!w-auto !rounded-lg !px-4 !py-2 !text-sm !font-medium"
              >
                Approve timesheets
              </Button>
            </div>
          )}
        </div>
      </Tabs>

      <div className="mt-auto flex items-center justify-between pt-4">
        <div className="text-sm text-gray-700">
          Showing {startIndex} to {endIndex} of {groups.length} results
          {!isPendingTab && isFetchingMore && (
            <span className="ml-2 text-gray-500">Loading more…</span>
          )}
          {!isPendingTab && isRefreshing && (
            <span className="ml-2 text-gray-500">Refreshing…</span>
          )}
          {isPendingTab && isMissingRefreshing && (
            <span className="ml-2 text-gray-500">Refreshing…</span>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage >= totalPages}
            className="rounded-md border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      <TimesheetGroupDrawer
        group={isPendingTab ? null : drawerGroup}
        isOpen={!isPendingTab && !!drawerGroup}
        payPeriod={selectedPayPeriod}
        canApprove={canApprove}
        showClientName={view.actions.showClientName}
        payCycleFrequency={payCycleFrequency}
        payRunCycleAnchorDate={payRunCycleAnchorDate}
        approvingCycleKey={approvingCycleKey}
        onClose={() => setDrawerRowKey(null)}
        onApproveCycle={approveDaySequenceIds}
        onTimesheetSaved={refetch}
      />
      <MissingTimesheetDrawer
        group={isPendingTab ? missingDrawerGroup : null}
        payPeriod={selectedPayPeriod}
        isOpen={isPendingTab && !!missingDrawerGroup}
        showClientName={view.actions.showClientName}
        canSubmit={view.actions.submitOnBehalf}
        onClose={() => setDrawerRowKey(null)}
        onSubmitShifts={submitMissingTimesheets}
      />
    </div>
  );
};

export default TimesheetsPage;
